import React, { useEffect, useState } from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import Card from '../../components/elements/Card';
import { fetchUserInfo } from '../../utils';
import Icon from '../../components/common/Icon';
import { useNavigate } from 'react-router-dom';
import Back from '../../components/common/Back';
import Image from '../../components/images/Image';
import Button from '../../components/elements/Button';
import Toast from '../../components/forms/Toast';
import ContactPersonNotice from '../../components/common/ContactPersonNotice';

const fallbackActivity = require('./../../assets/images/fallback/activity.png');

const renderStars = (val, max = 5) => {
  const stars = [];
  for (let i = 0; i < max; i++) {
    stars.push(
      <Icon
        key={i}
        name="star"
        theme={i < val ? 'primary' : 'disabled'}
        pack="sr"
      />
    );
  }
  return stars;
};

const Page = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(false);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    const selectedBattleId = sessionStorage.getItem('selectedBattleId');
    if (!selectedBattleId) {
      navigate('/battles');
      return;
    }
    fetchUserInfo('/battle', setUserInfo, setLoading, setError, ['selectedBattleId']);
  }, [reload, navigate]);

  const isCurrentJoker =
    userInfo?.joker?.id && userInfo.joker.id === sessionStorage.getItem('selectedBattleId');

  const handleCurrentJokerClick = () => {
    if (!userInfo?.joker?.id) return;
    sessionStorage.setItem('selectedBattleId', userInfo.joker.id);
    setReload((prev) => !prev);
    navigate('/battle');
  };

  const setJoker = async () => {
    try {
      const response = await fetch('/api/battle', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ joker_id: sessionStorage.getItem('selectedBattleId') }),
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Network response was not ok');
      }
      const data = await response.json();
      setError(data.message);
      setShowToast(true);
      setReload((prev) => !prev);
    } catch (err) {
      setError(err.message);
      setShowToast(true);
    }
  };

  const stats = [
    { key: 'endurance', value: userInfo?.endurance },
    { key: 'teamwork', value: userInfo?.teamwork },
    { key: 'guts', value: userInfo?.guts },
    { key: 'skill', value: userInfo?.skill },
  ];

  return (
    <Base activeNav="home">
      <Back to="/battles" />
      <ContactPersonNotice userInfo={userInfo} />

      <Card className={`battle-hero ${isCurrentJoker ? 'battle-hero--joker' : ''}`}>
        {userInfo?.is_water && (
          <span className="battle-hero__water-badge" title={t('water', { defaultValue: 'Water' })}>
            <Icon name="dewpoint" pack="sr" />
          </span>
        )}

        <div className="battle-hero__media">
          <Image
            url={userInfo?.photo_url ? userInfo.photo_url : fallbackActivity}
            className="circle battle-hero__image"
          />
          {isCurrentJoker && (
            <span className="battle-hero__joker-badge">
              <Icon name="star" pack="sr" theme="light" /> {t('joker')}
            </span>
          )}
        </div>

        <Title className="mt-3 mb-1" my="0">
          {userInfo?.name}
        </Title>

        {userInfo?.desc && (
          <p className="battle-hero__desc">{userInfo.desc}</p>
        )}

        {userInfo?.presented_by?.length > 0 && (
          <div className="battle-presented">
            <div className="battle-presented__label">{t('presentedBy')}</div>
            <div className="battle-presented__logos">
              {userInfo.presented_by.map((s) => (
                <div key={s.id} className="battle-presented__sponsor" title={s.name}>
                  {s.photo_url ? (
                    <img src={s.photo_url} alt={s.name} className="battle-presented__logo" />
                  ) : (
                    <span className="battle-presented__name">{s.name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {userInfo && (
          <div className="battle-stats">
            {stats.map(({ key, value }) => (
              <div key={key} className="battle-stat">
                <div className="battle-stat__label">{t(key)}</div>
                <div className="battle-stat__stars">{renderStars(value || 0)}</div>
              </div>
            ))}
          </div>
        )}

        {userInfo?.role === 'player' && userInfo?.is_joker && (
          <>
            <Button
              styles={{ fontWeight: 'bold' }}
              theme="outline-primary"
              mb="2"
              onClick={setJoker}
            >
              <Icon name="star" theme="primary" pack="sr" className="me-2" />
              {t('chooseJoker')}
            </Button>
            <p className="battle-joker-explain">{t('chooseJokerExplain')}</p>

            {userInfo.joker && !isCurrentJoker && (
              <Card to="/battle" onClick={handleCurrentJokerClick} className="battle-current-joker">
                <div className="battle-current-joker__label">{t('currentJoker')}</div>
                <div className="battle-current-joker__row">
                  <Image
                    url={
                      userInfo.joker.photo_url
                        ? userInfo.joker.photo_url
                        : fallbackActivity
                    }
                    className="circle"
                    size="2.5rem"
                  />
                  <span className="battle-current-joker__name">{userInfo.joker.name}</span>
                  <Icon name="angle-right" />
                </div>
              </Card>
            )}
          </>
        )}
      </Card>

      <Toast showToast={showToast} setShowToast={setShowToast} message={t(error)} />
    </Base>
  );
};

export default Page;
